import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Phone,
  Calendar,
  Shield,
  Heart,
  Star,
  ArrowRight
} from 'lucide-react'

const HeroSection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const stats = [
    { number: '10+', label: 'Yıl Deneyim' },
    { number: '24/7', label: 'Acil Hizmet' }
  ]

  const features = [
    {
      icon: Shield,
      title: 'Güvenli Tedavi',
      description: 'Modern ekipman ve deneyimli kadro'
    },
    {
      icon: Heart,
      title: 'Sevgi Dolu Bakım',
      description: 'Her pet için özel ilgi ve sevgi'
    },
    {
      icon: Star,
      title: 'Uzman Kadro',
      description: 'Alanında uzman veteriner hekimler'
    }
  ]

  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden bg-gradient-to-br from-primary-50 via-white to-accent-50">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      
      {/* Floating Elements */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 10, 0],
          x: [0, 30, 0],
          y: [0, -20, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute top-20 right-10 w-32 h-32 bg-gradient-to-br from-primary-200 to-primary-300 rounded-full opacity-30 blur-xl"
      />
      <motion.div
        animate={{
          scale: [1.1, 0.8, 1.1],
          rotate: [0, -15, 0],
          x: [0, -25, 0],
          y: [0, 25, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute bottom-20 left-10 w-24 h-24 bg-gradient-to-br from-accent-200 to-accent-300 rounded-full opacity-30 blur-xl"
      />
      <motion.div
        animate={{
          scale: [0.8, 1.3, 0.8],
          rotate: [0, 20, 0],
          x: [0, -40, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute top-1/2 left-20 w-16 h-16 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full opacity-25 blur-lg"
      />
      <motion.div
        animate={{
          scale: [1.2, 0.9, 1.2],
          rotate: [0, -25, 0],
          x: [0, 35, 0],
          y: [0, 40, 0],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute top-3/4 right-32 w-20 h-20 bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full opacity-25 blur-lg"
      />

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-12 items-center">
          {/* Left Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full"
            >
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">Şanlıurfa'nın En Güvenilir Veteriner Kliniği</span>
            </motion.div>

            {/* Main Heading */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="space-y-4"
            >
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight">
                Can Dostlarınız İçin
                <span className="text-gradient block">
                  En İyi Bakım
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl">
                Modern teknoloji ve deneyimli kadromuzla kedileriniz ve köpeklerinizin 
                sağlığını koruyoruz. 7/24 acil hizmet ile yanınızdayız.
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center justify-center space-x-2 group"
              >
                <Calendar className="w-5 h-5" />
                <span>Randevu Al</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <a
                href="tel:+904141234567"
                className="btn-secondary inline-flex items-center justify-center space-x-2"
              >
                <Phone className="w-5 h-5" />
                <span>Acil Arama</span>
              </a>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                  className="flex items-start space-x-3"
                >
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <feature.icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Image & Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            {/* Main Media */}
            <div className="relative">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl"
              >
                <video
                  src="/ardic-logo.mp4"
                  className="w-full h-[336px] object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster="/siyam-kedisi.webp"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </motion.div>

              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.56 }}
                animate={inView ? { opacity: 1, y: 0, scale: 0.7 } : {}}
                transition={{ duration: 0.8, delay: 0.9 }}
                whileHover={{ scale: 0.74, y: -5 }}
                className="absolute -bottom-8 -left-8 bg-gradient-to-br from-white via-primary-50 to-white rounded-2xl shadow-2xl p-6 border border-primary-200 backdrop-blur-sm origin-bottom-left"
              >
                <div className="grid grid-cols-1 gap-4">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={inView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                      className="text-center p-2 rounded-xl hover:bg-primary-100 transition-all duration-300"
                    >
                      <motion.div 
                        className="text-2xl font-bold text-primary-600"
                        animate={{ 
                          textShadow: [
                            "0 0 0px #2e6231",
                            "0 0 10px #2e6231",
                            "0 0 0px #2e6231"
                          ]
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        {stat.number}
                      </motion.div>
                      <div className="text-sm text-gray-600 font-medium">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Floating Doctor Image */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.1 }}
                className="absolute -top-8 -right-8 w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-xl"
              >
                <img
                  src="/siyam-kedisi.webp"
                  alt="Kedi"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-primary-400 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  )
}

export default HeroSection
